import React, { useEffect } from "react";
import styled from "styled-components";

// The detail surfaces the Today view demotes behind a click. Everything that
// used to be its own equally weighted tab opens here instead, so the default
// view stays one page that answers one question.

// Above the app shell (z 10), below the lead drawer and the throughput modal
// (z 1300) so a lane opened from inside the rail detail lands on top of it.
const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  padding: var(--space-5);
  background: rgba(0, 0, 0, 0.45);
  overflow-y: auto;
`;

const Shell = styled.div<{ $wide: boolean }>`
  width: ${(p) => (p.$wide ? "min(1040px, 100%)" : "min(680px, 100%)")};
  max-height: 90vh;
  margin: auto;
  display: flex;
  flex-direction: column;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-5) var(--space-5) var(--space-3);
  border-bottom: 1px solid var(--color-border-subtle);
`;

const Titles = styled.div`
  min-width: 0;
`;

const Title = styled.h2`
  font-family: var(--font-display);
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  margin: 0 0 4px;
`;

const Sub = styled.p`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  margin: 0;
`;

const CloseBtn = styled.button`
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border-radius: var(--radius-md);
  width: 30px;
  height: 30px;
  flex: none;
  cursor: pointer;
  font-size: 15px;
  line-height: 1;

  &:hover {
    color: var(--color-text);
  }
`;

const Body = styled.div`
  padding: var(--space-5);
  overflow-y: auto;
`;

const TabRow = styled.div`
  display: flex;
  gap: var(--space-2);
  padding: 0 var(--space-5) var(--space-3);
  border-bottom: 1px solid var(--color-border-subtle);
  flex-wrap: wrap;
`;

const TabBtn = styled.button<{ $active: boolean }>`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-full);
  cursor: pointer;
  border: 1px solid
    ${(p) => (p.$active ? "var(--color-accent)" : "var(--color-border)")};
  background: ${(p) =>
    p.$active ? "var(--color-accent)" : "var(--color-surface)"};
  color: ${(p) =>
    p.$active ? "var(--color-text-inverse)" : "var(--color-text-secondary)"};
`;

export type ModalView = { id: string; label: string };

export const Modal: React.FC<{
  title: string;
  subtitle?: string;
  wide?: boolean;
  views?: ModalView[];
  activeView?: string;
  onView?: (id: string) => void;
  onClose: () => void;
  // Set while another layer sits on top. Every layer listens on window, so
  // without this one Escape closes the modal AND whatever opened over it.
  escapeDisabled?: boolean;
  children: React.ReactNode;
}> = ({
  title,
  subtitle,
  wide = false,
  views,
  activeView,
  onView,
  onClose,
  escapeDisabled = false,
  children,
}) => {
  useEffect(() => {
    if (escapeDisabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, escapeDisabled]);

  return (
    <Backdrop
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Shell $wide={wide}>
        <Head>
          <Titles>
            <Title>{title}</Title>
            {subtitle && <Sub>{subtitle}</Sub>}
          </Titles>
          <CloseBtn type="button" aria-label="Close" onClick={onClose}>
            ×
          </CloseBtn>
        </Head>
        {views && views.length > 1 && (
          <TabRow role="tablist">
            {views.map((v) => (
              <TabBtn
                key={v.id}
                type="button"
                role="tab"
                aria-selected={activeView === v.id}
                $active={activeView === v.id}
                onClick={() => onView?.(v.id)}
              >
                {v.label}
              </TabBtn>
            ))}
          </TabRow>
        )}
        <Body>{children}</Body>
      </Shell>
    </Backdrop>
  );
};

export default Modal;
